import { useTooltip } from '@/hooks/useTooltip';
import { formatNumber } from '@/lib/dashboardFormat';

interface HorizontalBarListProps {
  items: { label: string; value: number }[];
  /** Подпись, когда показывать нечего. */
  emptyText?: string;
}

/** Строка рейтинга: тултип типовой, показывает полное название и значение. */
function BarRow({ label, value, max }: { label: string; value: number; max: number }) {
  const tooltip = useTooltip<HTMLLIElement>(`${label}: ${formatNumber(value)}`);

  return (
    <li className="hbar-list__row" ref={tooltip.ref} {...tooltip.handlers}>
      <span className="hbar-list__label">{label}</span>
      <div className="hbar-list__track">
        <div className="hbar-list__bar" style={{ width: `${(value / max) * 100}%` }} />
      </div>
      <span className="hbar-list__value">{formatNumber(value)}</span>
      {tooltip.node}
    </li>
  );
}

/** Горизонтальные полосы с подписью и значением – для рейтингов/использования. */
export function HorizontalBarList({ items, emptyText = 'Нет данных.' }: HorizontalBarListProps) {
  if (items.length === 0) return <p className="chart__empty">{emptyText}</p>;

  const max = Math.max(1, ...items.map((i) => i.value));

  return (
    <ul className="hbar-list">
      {items.map((item) => (
        <BarRow key={item.label} label={item.label} value={item.value} max={max} />
      ))}
    </ul>
  );
}

interface StatusBarSegment {
  label: string;
  value: number;
  tone: 'good' | 'warn' | 'crit' | 'neutral';
}

/** Сегмент полосы статусов с типовым тултипом. */
function Segment({ segment, widthPercent }: { segment: StatusBarSegment; widthPercent: number }) {
  const tooltip = useTooltip<HTMLDivElement>(`${segment.label}: ${formatNumber(segment.value)}`);

  return (
    <div
      ref={tooltip.ref}
      className={`status-bar__segment status-bar__segment--${segment.tone}`}
      style={{ width: `${widthPercent}%` }}
      {...tooltip.handlers}
    >
      {tooltip.node}
    </div>
  );
}

/**
 * Сегментированная полоса с легендой — разбивка кабинетов по статусу.
 * Нулевые сегменты в полосу не попадают (иначе видна лишняя граница), но в
 * легенде остаются: «демо: 0» — тоже информация.
 */
export function StatusBar({ segments }: { segments: StatusBarSegment[] }) {
  const total = segments.reduce((sum, s) => sum + s.value, 0);

  return (
    <div className="status-bar">
      <div className="status-bar__track">
        {total === 0 ? (
          <div className="status-bar__segment status-bar__segment--neutral" style={{ width: '100%' }} />
        ) : (
          segments
            .filter((s) => s.value > 0)
            .map((s) => <Segment key={s.label} segment={s} widthPercent={(s.value / total) * 100} />)
        )}
      </div>
      <ul className="status-bar__legend">
        {segments.map((s) => (
          <li key={s.label} className={`status-bar__legend-item status-bar__legend-item--${s.tone}`}>
            <span className="status-bar__dot" /> {s.label}
            <span className="status-bar__legend-value">{formatNumber(s.value)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
